import { Component, createRef } from 'react';
import { observable, action, makeObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import apiService from '@services/apiService';
import { modalService } from '@components/Modal';
import { PromiseCompletion } from '@classes/PromiseCompletion';

type ChatSuggestionsProps = {
  onSelect: (message: string, capability: string, imageFile?: File | null) => void;
};

type SuggestionItem = {
  id?: string;
  title: string;
  text?: string;
  capability?: 'vision' | 'tools' | 'none';
  isLiked?: boolean;
}

const greetingMessages = {
  general: [
    '{user}, how can I assist you today?',
    '{user}, what can I help you with?',
    '{user}, how can I make your day easier?',
    '{user}, what would you like to talk about?',
    '{user}, how can I support you today?',
    '{user}, what chaos shall we create today?',
    '{user}, what\'s on your mind?',
    'Welcome back, {user}!'],
  morning: ['{user}, good morning! How can I assist you today?',
    'Good morning, {user}!',
    'Morning! {user}, what can I help you with?',
    '{user}, how can I make your morning better?',
    'Good morning {user}, coffee in hand?',
    'Morning {user}, let\'s make it a good one',
    'Morning {user}, may your coffee be perfect today',
    '{user}, fresh start, fresh energy',
    '{user}, the sun is up, you should probably be too',
    'Morning, {user}!'
  ],
  afternoon: ['Good afternoon, {user}! How can I assist you today?',
    'Good afternoon, {user}!',
    'Afternoon! {user}, what can I help you with?',
    '{user}, hope you\'re enjoying your day',
    '{user}, how can I make your afternoon better?'
  ],
  evening: [
    'Good evening, {user}! How can I assist you tonight?',
    'Good evening, {user}',
    'Evening! {user}, what can I help you with?',
    '{user}, how can I make your evening better?',
    '{user}, what would you like to talk about this evening?',
    '{user}, how can I support you tonight?'
  ],
  night: [
    'Working late, {user}? How can I assist you tonight?',
    'Night owl! {user}, what can I help you with?',
    '{user}, how can I make your night better?',
    'Late night {user}, I\'m here with you',
    '{user}, what would you like to talk about tonight?',
    '{user}, how can I support you tonight?'
  ]
}

@observer
export default class ChatSuggestions extends Component<ChatSuggestionsProps> {

  @observable private _suggestions: SuggestionItem[] = [];
  private _fileInputRef = createRef<HTMLInputElement>();
  private _pendingVisionSuggestion: SuggestionItem | null = null;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _greeting = '';

  constructor(props: ChatSuggestionsProps) {
    super(props);
    makeObservable(this);


    const userName = window.user?.givenName || window.user?.name;
    const hour = new Date().getHours();
    let greetingCategory = 'general';
    if (hour >= 5 && hour < 12) {
      greetingCategory = 'morning';
    } else if (hour >= 12 && hour < 17) {
      greetingCategory = 'afternoon';
    } else if (hour >= 17 && hour < 22) {
      greetingCategory = 'evening';
    } else {
      greetingCategory = 'night';
    }

    // 20% - general greeting, 80% - time based greeting
    if (Math.random() < 0.2) {
      greetingCategory = 'general';
    }
    const greetings = greetingMessages[greetingCategory as keyof typeof greetingMessages];
    // name of user can be not available due to privacy settings. If it's available - update greeting to include it, if not - just use generic greeting
    const greeting = greetings[Math.floor(Math.random() * greetings.length)];
    // replace ', {user}' or '{user} ,' with user name of empty string if name is not available. Then spli by ?.! and trim + uppercase first letter
    let personalizedGreeting = greeting.replace(/,?\s*\{user\}\s*,?/, userName ? `, ${userName},` : '').split(/([.?!])/).map(part => {
      // trim [, ] characters from start and end of part, then uppercase first letter and join with rest of string. If part is only punctuation - return as is
      const trimmed = part.trim().replace(/^[,]+|[,]+$/g, '');
      return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : '';
    }).join(' ');

    //replace ' ?' and ' !' with '?' and '!' respectively
    this._greeting = personalizedGreeting.replace(/\s+([?!])/g, '$1');
  }

  componentDidMount(): void {
    void this._loadSuggestions();
  }

  render() {
    return (
      <div className="chat-starter">
        <h2>{this._greeting}</h2>
        {!!this._suggestions.length && <div className="starter-suggestions">
            {this._suggestions.map((item, index) => (
            <div key={item.id || index} className={`suggestion-item${item.isLiked ? ' liked' : ''}`}>
              <button
                  className="suggestion-btn"
                  onClick={() => this._onSuggestionClick(item)}
              >
                  {item.title}
              </button>
              {item.id && (
                <div className="suggestion-actions">
                  <button
                    className={`suggestion-action-btn like-btn${item.isLiked ? ' active' : ''}`}
                    onClick={(e) => this._onLikeClick(e, item)}
                    title={item.isLiked ? 'Unlike' : 'Like'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={item.isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                    </svg>
                  </button>
                  <button
                    className="suggestion-action-btn dislike-btn"
                    onClick={(e) => this._onDislikeClick(e, item)}
                    title="Dislike"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
            ))}
        </div>}
        {this._suggestions.length > 0 && <button disabled={this._loader.isPending}
          className="load-more-btn"
          onClick={() => void this._loadSuggestions()}
        >
          Load more...
        </button>}
        <input
          type="file"
          ref={this._fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={this._onImageSelected}
        />
      </div>
    );
  }

  private _onSuggestionClick = (item: SuggestionItem) => {
    if (item.capability === 'vision') {
      // For vision suggestions, open file picker first
      this._pendingVisionSuggestion = item;
      this._fileInputRef.current?.click();
    } else {
      this.props.onSelect(item.text || item.title, item.capability || '');
    }
  };

  private _onLikeClick = async (e: React.MouseEvent, item: SuggestionItem) => {
    e.stopPropagation();
    if (!item.id) return;
    const response = await apiService.post<{ success: boolean; isLiked: boolean }>(`/api/suggestions/${item.id}/like`, {}, { loader: this._loader });
    if (response.success) {
      runInAction(() => {
        const suggestion = this._suggestions.find(s => s.id === item.id);
        if (suggestion) {
          suggestion.isLiked = response.isLiked;
        }
      });
    }
  };

  private _onDislikeClick = async (e: React.MouseEvent, item: SuggestionItem) => {
    e.stopPropagation();
    if (!item.id) return;

    if (!await modalService.showConfirmation('Are you sure you want to dislike this suggestion? It will be removed.', 'Dislike Suggestion', { buttonTitle: 'Dislike', color: 'danger' })) {
      return;
    }
    await apiService.post<{ success: boolean; deleted: boolean }>(`/api/suggestions/${item.id}/dislike`, {}, { loader: this._loader });
    
    // Remove from local state
    runInAction(() => {
      this._suggestions = this._suggestions.filter(s => s.id !== item.id);
    });

    // Load one more suggestion to replace
    void this._loadSuggestions(1);
  };

  private _onImageSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && this._pendingVisionSuggestion) {
      this.props.onSelect(
        this._pendingVisionSuggestion.text || this._pendingVisionSuggestion.title, 
        this._pendingVisionSuggestion.capability || '',
        file
      );
    }
    this._pendingVisionSuggestion = null;
    // Reset input so same file can be selected again
    if (this._fileInputRef.current) {
      this._fileInputRef.current.value = '';
    }
  };

  @action
  private async _loadSuggestions(maxCount: number = 5) {
    const response = await apiService.get<{ suggestions: SuggestionItem[] }>(`/api/suggestions?maxCount=${maxCount}`, { loader: this._loader });

    runInAction(() => {
      if (maxCount === 1) {
        // Append single suggestion
        this._suggestions.push(...response.suggestions);
      } else {
        this._suggestions = response.suggestions;
      }
    });
  }

}